import type { IUser } from "@/core/auth";
import { manualEditsReviewCount } from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import { useCellCommentThreads } from "@/payroll/ai-script/cell-comments/query";
import { useGetAiScriptCellOverrides } from "@/payroll/ai-script/query";
import {
  canRequestSheetApprovals,
  useGetSheetApprovalRequestState,
} from "@/payroll/pay-breakdown/sheet-approval-requests";
import type { PayBreakdownPeriodState } from "./use-pay-breakdown-period";

type UsePayBreakdownReviewStateParams = {
  businessId: string | null;
  user: IUser | null;
} & Pick<PayBreakdownPeriodState, "business" | "aiScriptRunId">;

export const usePayBreakdownReviewState = ({
  businessId,
  user,
  business,
  aiScriptRunId,
}: UsePayBreakdownReviewStateParams) => {
  const { data: commentThreads } = useCellCommentThreads(
    businessId,
    aiScriptRunId,
  );
  const openCommentCount =
    commentThreads?.filter((thread) => !thread.resolved_at).length ?? 0;
  const { data: cellOverridesData } = useGetAiScriptCellOverrides(
    businessId,
    aiScriptRunId,
  );
  const manualEditsToReview = manualEditsReviewCount(
    cellOverridesData?.overrides ?? [],
  );

  const canRequestApprovals = canRequestSheetApprovals(business, user);
  const { data: sheetApprovalState } = useGetSheetApprovalRequestState(
    businessId,
    aiScriptRunId,
    canRequestApprovals,
  );

  return {
    openCommentCount,
    manualEditsToReview,
    canRequestApprovals,
    sheetApprovalState,
  };
};
